import React, { forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { usePDF } from "react-to-pdf";
import styled from "@emotion/styled";
import Markdown from "./Markdown";
import { withDownloadToast } from "../../utils/exports/withDownloadToast";

const PDFContent = ({ title, data, chartImage }) => {
  return (
    <div
      style={{
        padding: "40px",
        color: "#000000",
        fontSize: "18px",
        lineHeight: 1.6,
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#ffffff",
      }}
    >
      <h1
        style={{
          color: "#5b6bfd",
          fontSize: "24px",
          fontWeight: 600,
          marginBottom: "30px",
          textAlign: "center",
        }}
      >
        {title} AI Insights
      </h1>
      {chartImage && (
        <div
          style={{
            width: "100%",
            marginBottom: "30px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src={chartImage}
            alt="Visualization"
            style={{
              maxWidth: "100%",
              height: "auto",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          />
        </div>
      )}
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Markdown>{data}</Markdown>
      </div>
    </div>
  );
};

PDFContent.propTypes = {
  data: PropTypes.string,
  title: PropTypes.string,
  chartImage: PropTypes.string,
};

const OffScreenContainer = styled.div`
  position: fixed;
  left: -9999px;
  top: 0;
  width: 1200px; // Fixed width for PDF generation
  height: auto;
  overflow: visible;
  background: white;
`;

/**
 * PDF Export Section Component
 * Handles PDF generation and export functionality
 * @param {string} title - Title for the PDF
 * @param {string} data - Content data to include in PDF
 * @param {string} chartImage - Base64 image data for chart
 * @param {function} onExport - Callback when export is triggered
 */
const PdfExportSection = forwardRef(
  ({ title, data = "", chartImage = "", onExport = null }, ref) => {
    const filename = title + " AI Summary.pdf";
    const { toPDF, targetRef } = usePDF({ filename });

    const handleExport = () => {
      withDownloadToast(filename, () => toPDF()).catch(() => {
        /* toast already shown by withDownloadToast */
      });
      if (onExport) {
        onExport();
      }
    };

    useImperativeHandle(ref, () => ({
      handleExport,
    }));

    return (
      <>
        <OffScreenContainer>
          <div ref={targetRef}>
            <PDFContent title={title} data={data} chartImage={chartImage} />
          </div>
        </OffScreenContainer>
      </>
    );
  }
);

PdfExportSection.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.string,
  chartImage: PropTypes.string,
  onExport: PropTypes.func,
};

export default PdfExportSection;
